import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/StatusBadge';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { AlertTriangle, Zap } from 'lucide-react';

export default function DoctorQueue() {
  const { packetTypes } = useApp();
  const [items, setItems] = useState([]);
  const [amendments, setAmendments] = useState([]);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const r = await api.get('/doctor/queue');
    setItems(r.data || []);
    // Delivered packets are in no queue, so a patient's change request on one
    // would otherwise have no way in front of the doctor who must re-sign it.
    api.get('/doctor/amendments').then((a) => setAmendments(a.data.cases || [])).catch(() => setAmendments([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <h1 className="text-base lg:text-lg font-semibold tracking-tight">Doctor Review Queue</h1>
        <span className="text-xs text-muted-foreground">{items.length} item(s)</span>
        <div className="flex-1" />
        {items.length > 0 && (
          <Button size="sm" onClick={() => navigate('/doctor/fast-sign')} data-testid="doctor-fast-sign-mode-button">
            <Zap className="h-4 w-4 mr-1.5" /> Fast-Sign mode
          </Button>
        )}
      </div>
      {amendments.length > 0 && (
        <Card className="p-4 mb-4 border-[hsl(45_93%_84%)] bg-[hsl(48_96%_92%)]" data-testid="doctor-amendments">
          <div className="text-sm font-medium text-[hsl(32_92%_28%)] mb-2">Changes requested on signed packets ({amendments.length})</div>
          <ul className="space-y-2">
            {amendments.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center gap-2 rounded-md border bg-card px-3 py-2" data-testid={`doctor-amendment-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{c.patient_name || '—'}</div>
                  <div className="text-xs text-muted-foreground truncate">{(c.requests || []).map((r) => r.message).join(' · ')}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => navigate(`/doctor/review/${c.id}`)} data-testid={`doctor-amendment-open-${c.id}`}>Review & re-sign</Button>
              </li>
            ))}
          </ul>
        </Card>
      )}
      {/* Mobile: card list */}
      <div className="md:hidden space-y-3" data-testid="doctor-queue-cards">
        {items.length === 0 && <Card className="p-8 text-center text-sm text-muted-foreground">No documents awaiting review.</Card>}
        {items.map((it) => {
          const pt = packetTypes.find(p => p.id === it.packet_type_id)?.label || it.packet_type_id;
          const due = it.delivery?.due_date || '';
          const overdue = it._overdue;
          return (
            <Card key={it.id} className={`p-4 space-y-2 ${overdue ? 'border-l-2 border-l-[hsl(0_72%_52%)]' : ''}`} data-testid={`doctor-queue-card-${it.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium truncate">{it.patient_form?.full_name || '—'}</p>
                  <p className="text-xs text-muted-foreground truncate">{pt} · by {it.created_by_name || 'Medical staff'}</p>
                </div>
                <StatusBadge status={it.status} />
              </div>
              {due && (
                <p className={`text-xs ${overdue ? 'text-[hsl(0_72%_40%)] font-medium' : 'text-muted-foreground'}`}>
                  {overdue && <AlertTriangle className="h-3 w-3 inline mr-1" />}Due {due}{overdue ? ' — overdue' : ''}
                </p>
              )}
              <Button size="sm" className="w-full" onClick={() => navigate(`/doctor/review/${it.id}`)} data-testid={`doctor-open-card-${it.id}`}>Review</Button>
            </Card>
          );
        })}
      </div>

      {/* Desktop: table */}
      <Card className="hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="doctor-queue-table">
            <thead className="bg-card border-b">
              <tr className="text-left text-xs text-muted-foreground">
                <th className="px-3 py-2">Patient</th>
                <th className="px-3 py-2">Packet</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Due</th>
                <th className="px-3 py-2">Sent</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">No documents awaiting review.</td></tr>}
              {items.map((it) => {
                const pt = packetTypes.find(p => p.id === it.packet_type_id)?.label || it.packet_type_id;
                const due = it.delivery?.due_date || '';
                const overdue = it._overdue;
                return (
                  <tr key={it.id} className={`border-b hover:bg-muted/60 ${overdue ? 'border-l-2 border-l-[hsl(0_72%_52%)]' : ''}`} data-testid={`doctor-queue-row-${it.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{it.patient_form?.full_name || '—'}</div>
                      <div className="text-xs text-muted-foreground">Prepared by {it.created_by_name || 'Medical staff'}</div>
                    </td>
                    <td className="px-3 py-2">{pt}</td>
                    <td className="px-3 py-2"><StatusBadge status={it.status} /></td>
                    <td className="px-3 py-2">
                      {due ? (<span className={overdue ? 'text-[hsl(0_72%_40%)] font-medium' : ''}>{overdue && <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />}{due}</span>) : <span className="text-muted-foreground text-xs">—</span>}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{(it.updated_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-3 py-2"><Button size="sm" onClick={() => navigate(`/doctor/review/${it.id}`)} data-testid={`doctor-open-${it.id}`}>Review</Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
